<template>
	<CardSlashed v-if="menuStore.loadedAndNormalizedMenu && (!selectableMenus || !selectableMenus?.length)">
		<template #left>
			<div class="col-12 centered p-10">
				<p class="cta-no-menu-text">{{getString('create_first_menu', [userStore.menuWord])}}</p>
			</div>
		</template>
		<template #right>
			<div class="mt-auto p-20">
				<p class="mb-20 cta-no-menu-right" v-html="getString('create_first_menu_hint', [userStore.menuWord])"></p>
				<AppButton @click="clickAddNewMenu" :text="getString('create_first_menu_btn')" />
			</div>
		</template>
	</CardSlashed>
	<div v-if="selectableMenus && selectableMenus?.length" class="menu-select-section">
		<div class="menu-select-section-row">
			<div class="menu-select-filter-container">
				<div
					class="menu-select-item"
					v-for="(menuName, menuIndex) in selectableMenus"
					:class="{active: menuIndex === selectedMenuIndex}"
					:key="menuIndex"
					@click="changeMenu(menuIndex)"
				><p>{{ menuName || userStore.menuWord }}</p></div>
			</div>
		</div>
		<div class="menu-select-action" @click="openMenuSettings">
			<img :src="'https://img.qromo.io/img/icons/icon-settings-primary'+(userStore.darkMode ? '-dark' : '')+'.svg'" class="icon-dimension-15"/>
		</div>
		<div class="menu-select-action" @click="clickAddNewMenu">
			<img :src="'https://img.qromo.io/img/vue/icons/icon-plus'+(userStore.darkMode ? '-dark' : '')+'.svg'" class="icon-dimension-15"/>
		</div>
	</div>
	<AppCard v-if="selectableMenus && selectableMenus?.length" no-header class="card card-margin menu-wrapper-card" :class="{'multi-select-active': multiSelectMode}">
		<template v-slot:body>
			<div class="menu-edit-body">
				<div class="menu-editor-container">
					<MenuEditor ref="menu-editor" @item-saved="refreshPreview" @category-saved="refreshPreview" />

					<AiButton class="mobile-ai-btn" v-if="noCategories" @click="menuStore.aiToolsActive = true" :text="getString('generate_x', [userStore.menuWord])" icon-left="icon-ai-primary" outline />
					<AiButton class="mobile-ai-btn" v-if="canGenerateImages" @click="menuStore.aiImagesToolsActive = true" :text="getString('generate_images')" icon-left="icon-ai-primary" outline />

					<CardsHelp v-if="showCardsHelp" class="mt-35" />
				</div>
				<div class="menu-preview-container">
					<AiButton class="m-auto" v-if="noCategories" @click="menuStore.aiToolsActive = true" :text="getString('generate_x', [userStore.menuWord])" icon-left="icon-ai-primary" outline />
					<AiButton class="m-auto" v-if="canGenerateImages" @click="menuStore.aiImagesToolsActive = true" :text="getString('generate_images')" icon-left="icon-ai-primary" outline />
					<MenuPreview ref="menu-preview" />
				</div>
			</div>
		</template>
	</AppCard>
	<AppCard :title="getString('variations')" v-if="foundVariationsCategories" class="card card-margin" :class="{'multi-select-active': multiSelectMode}">
		<template v-slot:body>
			<MenuEditor variations-only ref="menu-editor" @item-saved="refreshPreview" @category-saved="refreshPreview" />
		</template>
	</AppCard>
	<AppPopup
		:title="getString('new_menu_name', [userStore.menuWord])"
		:buttons="[{text: getString('create'), callback: addNewMenu}]"
		ref="confirm-new-menu"
	>
		<div class="container-input mb-15">
			<p class="popup-body-text mb-15">
				{{ getString('new_menu_hint', [userStore.menuWord]) }}
			</p>
			<AppTextInput ref="new-menu-name-input" @pressedEnter="addNewMenu" :value="newMenuName" @changingText="newName=>{newMenuName=newName}" :placeholder="getString('menu_name_example', [userStore.menuWord])" />
		</div>
	</AppPopup>
	<AppBottomSheet :force-width="bottomSheetWidth" :active="menuSettingsId !== null"  @toggling-popup="toggleMenuSettings" >
		<MenuSettingsEditor v-if="menuSettingsId !== null" @saved="toggleMenuSettings(null)" />
	</AppBottomSheet>
	<AppBottomSheet :undismissable="menuStore.aiToolsLoading" :force-width="bottomSheetSmallWidth" :active="menuStore.aiToolsActive" @toggling-popup="toggleAITools">
		<AITools v-if="menuStore.aiToolsActive" />
	</AppBottomSheet>
	<AppBottomSheet :undismissable="loadingAIImages" :force-width="bottomSheetSmallWidth" :active="menuStore.aiImagesToolsActive" @toggling-popup="toggleAIImagesTools">
		<AIImagesTools v-if="menuStore.aiImagesToolsActive" @loading="v => {loadingAIImages = v}" @close="closeAIImagesTools" />
	</AppBottomSheet>
</template>

<script>
import AppCard from "@/shared/components/AppCard.vue";
import AppButton from "@/shared/components/AppButton.vue";
import AiButton from "@/console-new/components/shared/AiButton.vue";
import MenuEditor from "@/console-new/components/config/menu/MenuEditor.vue";
import MenuPreview from "@/console-new/components/config/menu/MenuPreview.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";

import {useLanguageStore, useCurrencyStore, useNotificationStore} from "@/shared/stores";
import {useUserStore, useMenuStore} from "@/console-new/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import MenuSettingsEditor from "@/console-new/components/config/menu/MenuSettingsEditor.vue";
import CardsHelp from "@/console-new/components/config/settings/menu/CardsHelp.vue";
import AITools from "@/console-new/components/config/menu/AITools.vue";
import AIImagesTools from "@/console-new/components/config/menu/AIImagesTools.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AvailabilityDot from "@/console-new/components/config/menu/AvailabilityDot.vue";
import CardSlashed from "@/console-new/components/shared/CardSlashed.vue";
import AppPopup from "@/shared/components/AppPopup.vue";
import TouchNumberInput from "@/console-new/components/shared/TouchNumberInput.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let menuStore = useMenuStore();
		let userStore = useUserStore();
		let notificationStore = useNotificationStore();
		let currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, currencyStore, showNotification: notificationStore.showNotification, menuStore, userStore};
	},
	name: "MenuContainer",
	inject: ['providedAction'],
	components: {
		AppTextInput,
		CardSlashed,
		TouchNumberInput,
		AiButton,
		AppPopup,
		AvailabilityDot, AppButtonIcon, AITools, AIImagesTools, MenuSettingsEditor, AppBottomSheet, AppCard, AppButton, MenuEditor, MenuPreview, CardsHelp},
	data() {
		return {
			multiSelectMode: false,
			menuSettingsId: null,
			newMenuName: null,
			lastActiveMenuName: null,
			loadingAIImages: false,
			loading: false
		};
	},
	mixins: [WindowWidth],
	computed: {
		activeMenu() {
			return (this.selectedMenuIndex || this.selectedMenuIndex === 0) ? this.sortedMenus.find(men => men.menu_id === this.menuStore?.modMenu?.menu_id) : null;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else if(this.windowWidth < 1440) {
				return "960px";
			} else {
				return "1280px";
			}
		},
		bottomSheetSmallWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
		showCardsHelp() {
			let isBusinessRegisteredFromMoreThan2Weeks = this.userStore.currBusiness && this.userStore.currBusiness.registrationDate && ((new Date()).getTime() - this.userStore.currBusiness.registrationDate.getTime()) > 1000 * 60 * 60 * 24 * 14;
			let menuHasManyItems = this.menuStore.itemsNum > 7;
			return !!this.userStore.currBusiness && this.menuStore.loadedAndNormalizedMenu && !isBusinessRegisteredFromMoreThan2Weeks && !menuHasManyItems;
		},
		selectableMenus() {
			return this.sortedMenus.map(r => r.name || this.userStore.menuWord);
		},
		foundVariationsCategories() {
			if(!this.menuStore?.modMenu.categories) { return false; }
			for (let catId in this.menuStore.modMenu.categories) {
				let category = this.menuStore.modMenu.categories[catId];
				if(!!category.variations_only) {
					return true;
				}
			}
			return false;
		},
		noCategories() {
			if(!this.menuStore.loadedBusinessMenu || !this.menuStore.loadedAndNormalizedMenu) {
				return false;
			}
			return !this.menuStore?.modMenu.categories || Object.keys(this.menuStore?.modMenu.categories).length === 0;
		},
		canGenerateImages() {
			if(!this.menuStore.loadedBusinessMenu || !this.menuStore.loadedAndNormalizedMenu) {
				return false;
			}
			if(this.noCategories) { return false; }
			if(this.userStore.maxAIGeneratedImagesPerMonth <= 0) { return false; }
			return !!this.menuStore?.modMenu?.canGenerateImages;
		},
		sortedMenus() {
			return this.menuStore.sortedMenus;
		},
		selectedMenuIndex() {
			let index = 0;
			for(let i = 0; i < this.sortedMenus.length; i++) {
				if(this.sortedMenus[i].menu_id === this.menuStore.modMenu.menu_id) {
					index = i;
					break;
				}
			}
			return index;
		},
	},
	methods: {
		closeAIImagesTools() {
			this.menuStore.aiImagesToolsActive = false;
			this.loadingAIImages = false;
		},
		initStartingMenu() {
			let menuIndex = this.menuStore.getMenuIndex(localStorage.getItem("last_menu_id"));
			if(menuIndex) {
				this.changeMenu(menuIndex);
			}
		},
		refreshPreview() {
			this.$refs["menu-preview"].refresh();
		},
		clickSave() {
			if (this.loading) {
				return;
			}
			this.providedAction.toggleMultiSelect(false);
			this.loading = true;
			let self = this;
			this.menuStore.saveMenu(function (success, errorDetails) {
				if(success) {
					if(self.$refs["menu-editor"]) {
						self.$refs["menu-editor"].closeAllCategories();
					}
					self.showNotification("menu-saved");
					setTimeout(() => {
						self.menuStore.refreshMenusData(()=>{
							self.loading = false;
						});
					}, 500);
				} else {
					self.loading = false;
					if(errorDetails === "item_price_mode") {
						let minHidePricesValue = self.currencyStore.readableCurrencyValue(self.currencyStore.currCurrency.min_hide_prices_amount)
						self.showNotification("item-price-mode", [minHidePricesValue]);
					} else if(errorDetails === "slug_mismatch") {
						self.showNotification("item-slug-error");
					} else if(errorDetails === 'auth_denied') {
						self.showNotification("auth-denied-error");
					} else if(errorDetails === "no_mass_delete"){
						self.showNotification("no-mass-delete");
					} else {
						self.showNotification("generic-fail");
					}
				}
			});
		},
		toggleMenuSettings(isOpen) {
			if(!isOpen) {
				this.menuSettingsId = null;
				if(isOpen === null) {
					this.$refs["menu-preview"].refresh();
				}
			}
		},
		toggleAITools(isOpen) {
			this.menuStore.aiToolsActive = isOpen;
		},
		toggleAIImagesTools(isOpen) {
			this.menuStore.aiImagesToolsActive = isOpen;
		},
		changeMenu(i) {
			this.providedAction.toggleMultiSelect(false);
			let menus = this.sortedMenus;
			if(i < menus.length) {
				let selectedMenu = menus[i];
				if(selectedMenu) {
					this.menuStore.changeModMenu(selectedMenu.menu_id);
				}
			}
		},
		focusNewMenuNameInput() {
			try {
				if(this.$refs && this.$refs["new-menu-name-input"]) {
					this.$refs["new-menu-name-input"].focus();
				}
			} catch (e) {
				console.warn('No vat input found: ', e)
			}
		},
		addNewMenu() {
			if(!this.newMenuName || !this.newMenuName?.length || this.newMenuName.length < 2) {
				this.showNotification("new-menu-name-too-short-fail");
				this.focusNewMenuNameInput();
				return;
			}
			if(this.$refs?.["confirm-new-menu"] && this.$refs["confirm-new-menu"]?.toggleStatus) {
				this.$refs["confirm-new-menu"].toggleStatus(false);
			}
			this.menuStore.addNewMenu(this.newMenuName, () => {
				this.openMenuSettings();
				this.newMenuName = null;
			});
		},
		clickAddNewMenu() {
			this.newMenuName = null;
			if(this.$refs?.["confirm-new-menu"] && this.$refs["confirm-new-menu"]?.toggleStatus) {
				this.$refs["confirm-new-menu"].toggleStatus(true);
			}
			setTimeout(this.focusNewMenuNameInput, 100);
		},
		openMenuSettings() {
			let menu = this.sortedMenus[this.selectedMenuIndex];
			if(menu) {
				this.menuSettingsId = menu.menu_id;
			}
		},
	},
	created() {
		if(!this.menuStore.loadedBusinessMenu && this.menuStore.currBusinessId !== null) {
			this.menuStore.loadMenu(this.initStartingMenu);
		}
		if(this.couponsStore) {
			this.couponsStore.getBusinessCoupons();
		}
	},
	watch: {
		'userStore.currBusinessId': function (newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.menuStore.loadMenu();
			}
		},
		providedAction(newProvidedObject) {
			if(newProvidedObject.menuToSave) {
				this.clickSave();
				this.providedAction.resetMenuToSave();
			}
			if(newProvidedObject.hasOwnProperty('multiSelectMode') && newProvidedObject.multiSelectMode !== this.multiSelectMode) {
				this.multiSelectMode = newProvidedObject.multiSelectMode;
			}
			if(newProvidedObject.hasOwnProperty('openMenu') && newProvidedObject.openMenu !== null) {
				this.menuSettingsId = newProvidedObject.openMenu;
				this.providedAction.resetOpenMenu();
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@use "@/console-new/assets/scss/_variables.scss" as *;

.menu-select-section {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	position: relative;
	padding: 10px;
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	border-radius: 0 0 13px 13px;
	background: rgba(var(--rgb-bg-primary-1), 67%);
	box-shadow: inset -3px -3px 6px var(--color-box-shadow-2);
	gap: 10px;


	.menu-select-action {
		border-radius: 13px;
		border: 1px solid $primary;
		display: flex;
		justify-content: center;
		align-items: center;
		padding: 9px;
		transition: background-color 0.25s ease-in-out;
		cursor: pointer;
		flex-shrink: 0;

		img {
			width: 16px;
			height: 16px;
		}

	}

	.menu-select-section-row {
		display: flex;
		flex-direction: row;
		align-items: center;
		overflow-x: auto;
		-ms-overflow-style: none;
		scrollbar-width: none;
		border-radius: 13px;
		background-color: var(--color-bg-primary-2);
		box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
		min-height: 51px;
		flex-grow: 1;

		.menu-select-filter-container {
			border-radius: 13px;
			display: flex;
			flex-direction: row;
			justify-content: flex-start;
			align-items: center;
			padding: 8px;
			gap: 6px;
			width: 100%;
			height: 66px;

			.menu-select-item {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				padding: 7px 12px;
				width: fit-content;
				cursor: pointer;
				user-select: none;
				transition: background-color 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;
				height: 50px;
				flex: 1;
				min-width: 140px;
				max-width: 250px;

				p {
					@include truncateString(1);
					font-size: 15px;
					color: var(--color-primary);
				}

				&.active {
					background-color: var(--color-active-button);

					p {
						color: var(--color-active-button-text);
					}
				}
			}

			@media (max-width: 900px) {
				padding: 8px;
				gap: 8px;
				min-height: 56px;

				.menu-select-item {
					padding: 7px 8px;
				}
			}

		}

	}
}
.multi-select-icon-button {
	visibility: hidden;
	&.show {
		visibility: visible;
	}
}
.card {
	transition: margin-bottom .5s ease;
	margin-bottom: 0;

	&.multi-select-active:last-of-type {
		margin-bottom: 70px;
		@media (max-width: 400px) {
			margin-bottom: 100px;
		}
	}
}

.availability-container {
	display: flex;
	justify-content: flex-start;
	align-items: center;
	gap: 8px;
}

.actions-container {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 8px;
}

.menu-edit-body {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	position: relative;
	padding: 10px 0;

	.menu-editor-container {
		width: calc(100% - 500px);
		position: relative;

		&::after {
			content: "";
			display: block;
			width: 1px;
			height: 100%;
			position: absolute;
			top: 0;
			right: 0;
			background: var(--color-alpha-3);
			z-index: 1;
		}

		.mobile-ai-btn {
			display: none;
			margin: 26px auto 0 auto;
		}

		@media(max-width: 1900px) {
			width: calc(100% - 400px);
		}

		@media(max-width: 1500px) {
			width: calc(100% - 320px);
		}

		@media(max-width: 1200px) {
			width: calc(100% - 230px);
		}
		@media(max-width: 960px) {
			width: calc(100% - 210px);
		}

		@media(max-width: 768px) {

			.mobile-ai-btn {
				display: block;
			}

			width: 100%;
			&::after {
				display: none;
			}
		}

		@media(max-width: 400px) {
			.mobile-ai-btn {
				@include centered;
				width: calc(100% - 16px);
			}
		}
	}

	.menu-preview-container {
		width: 500px;
		position: sticky;
		display: flex;
		justify-content: center;
		top: 24px;
		padding: 0 68px 0 80px;
		box-sizing: border-box;
		flex-direction: column;
		gap: 16px;

		.base-button {
			width: fit-content;
  			margin: 0 auto;
		}

		@media(max-width: 1900px) {
			width: 400px;
			padding: 0 18px 0 30px;
		}

		@media(max-width: 1500px) {
			width: 320px;
			padding: 0 18px 0 30px;
		}

		@media(max-width: 1200px) {
			width: 230px;
			padding: 0 0 0 12px;
		}

		@media(max-width: 960px) {
			width: 210px;

			.container-preview {
				padding: 0;
			}
		}

		@media(max-width: 768px) {
			display: none;
		}

	}
}

.card-margin {
	margin-top: $mainPadding;
}
.cta-no-menu-text {
	font-size: 24px;
	color: var(--color-bg);
	font-style: italic;
	font-weight: bold;
}
.cta-no-menu-right {
	font-size: 14px;
	line-height: 18px;
	text-align: left;
}
</style>